import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Composant Input débounced pour éviter la perte de focus
 * Spécialement conçu pour les champs numériques
 */
export const DebouncedNumberInput = React.memo(({ 
  name, 
  value, 
  onChange, 
  style, 
  min, 
  max, 
  step, 
  placeholder,
  label,
  debounceDelay = 300
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const timeoutRef = useRef(null);
  const lastValueRef = useRef(value);

  // Synchroniser avec la valeur externe seulement si elle change vraiment
  useEffect(() => {
    if (value !== lastValueRef.current && value !== localValue) {
      console.log(`🔄 DebouncedInput ${name}: sync externe ${lastValueRef.current} → ${value}`);
      setLocalValue(value || '');
      lastValueRef.current = value;
    }
  }, [value, localValue, name]);

  const handleLocalChange = useCallback((e) => {
    const newValue = e.target.value;
    console.log(`⚡ DebouncedInput ${name}: local change ${localValue} → ${newValue}`);
    
    setLocalValue(newValue);
    
    // Debounce l'envoi vers le parent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      console.log(`📤 DebouncedInput ${name}: envoi vers parent après debounce: ${newValue}`);
      onChange({ target: { name, value: newValue, type: 'number' } });
      lastValueRef.current = newValue;
    }, debounceDelay);
  }, [name, onChange, localValue, debounceDelay]);

  const handleFocus = useCallback((e) => {
    console.log(`🎯 FOCUS ${name}:`, e.target.value);
  }, [name]);

  const handleBlur = useCallback((e) => {
    console.log(`❌ BLUR ${name}:`, e.target.value);
    // Force l'envoi immédiat au blur
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      onChange({ target: { name, value: e.target.value, type: 'number' } });
      lastValueRef.current = e.target.value;
    }
  }, [name, onChange]);

  // Nettoyage du timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: '6px',
          fontWeight: '600',
          color: '#495057',
          fontSize: 'clamp(13px, 2.5vw, 15px)'
        }}>
          {label}
        </label>
      )}
      <input
        type="number"
        name={name}
        value={localValue}
        onChange={handleLocalChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={style}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        inputMode="decimal"
      />
    </div>
  );
});

DebouncedNumberInput.displayName = 'DebouncedNumberInput';

export default DebouncedNumberInput;